/**
 * Budget · Overview: pul oqimi (docs/12-moliya.md). Hisoblardagi pul, oy kirim/chiqimi va sofi, 12 oylik grafik,
 * chiqim tarkibi va "to'lanishi kerak / kutilayotgan kirim". Hamma summa USD da (UZS -- yozuv kuni kursi bilan).
 */
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Bar, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { dayjs } from "@/lib/utils";
import { categoryLabel, changePct, fmt, num } from "@/features/finance/money";
import type { FinanceOverview } from "@/features/finance/types";
import { Card, CardBody, CardHeader, KpiCard } from "@/components/ui/card";

// Grafik ranglari -- faqat tokenlar (tungi rejimda o'zi almashadi)
const PALETTE = [
  "var(--color-primary)",
  "var(--color-budget)",
  "var(--color-success)",
  "var(--color-danger)",
  "var(--color-chart)",
  "var(--color-warning)",
  "var(--color-ink-soft)",
  "var(--color-muted)",
];
const TOOLTIP = { fontSize: 12, borderRadius: 6, borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)", color: "var(--color-ink)" };
const AXIS = { fontSize: 11, fill: "var(--color-muted)" };

/** Qisqa summa o'qda: $12k, $1.2M */
const short = (v: number) => (Math.abs(v) >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`);

export function CashKpis({ o, month }: { o: FinanceOverview; month: string }) {
  const { t } = useTranslation("budget");
  const { t: ti } = useTranslation("metrics");
  const inPct = changePct(num(o.month_in), num(o.prev_in));
  const outPct = changePct(num(o.month_out), num(o.prev_out));
  const net = num(o.month_net);
  const vs = (pct: number | null) => (pct === null ? t("cash.noPrev") : t("cash.vsPrev", { pct: pct > 0 ? `+${pct}` : pct }));
  return (
    <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
      <KpiCard to="/budget/money" info={ti("cash.onAccounts")} label={t("cash.onAccounts")} value={fmt(o.total_usd, "USD")} sub={o.totals.length ? o.totals.map((x) => fmt(x.balance, x.currency)).join(" · ") : t("cash.noAccounts")} />
      <KpiCard to="/budget/money?flow=in" info={ti("cash.in")} label={t("cash.in", { month })} value={<span className="text-success">{fmt(o.month_in, "USD")}</span>} sub={vs(inPct)} />
      <KpiCard to="/budget/money?flow=out" info={ti("cash.out")} label={t("cash.out", { month })} value={fmt(o.month_out, "USD")} sub={vs(outPct)} subTone={outPct !== null && outPct > 20 ? "danger" : "muted"} />
      <KpiCard
        to="/budget/money"
        info={ti("cash.net")} label={t("cash.net", { month })}
        value={<span className={net < 0 ? "text-danger" : "text-success"}>{fmt(net, "USD")}</span>}
        sub={o.missing_rate ? t("cash.missingRate") : t("cash.netSub")}
        subTone={o.missing_rate ? "danger" : "muted"}
      />
    </div>
  );
}

export function CashflowChart({ o }: { o: FinanceOverview }) {
  const { t } = useTranslation("budget");
  const data = o.series.map((s) => ({ label: dayjs(`${s.period}-01`).format("MMM"), in: num(s.in), out: num(s.out), net: num(s.net) }));
  const empty = data.every((d) => d.in === 0 && d.out === 0);
  return (
    <Card className="xl:col-span-2">
      <CardHeader title={t("cash.chartTitle")} subtitle={t("cash.chartSub")} />
      <CardBody>
        {empty ? (
          <p className="py-10 text-center text-[13px] text-muted">{t("cash.chartEmpty")}</p>
        ) : (
          <div className="h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={AXIS} axisLine={{ stroke: "var(--color-line)" }} tickLine={false} />
                <YAxis tick={AXIS} axisLine={false} tickLine={false} tickFormatter={short} width={48} />
                <Tooltip cursor={{ fill: "var(--color-sidebar)" }} contentStyle={TOOLTIP} formatter={(v, name) => [fmt(Number(v), "USD"), t(`cash.series.${String(name)}`)]} />
                <Legend formatter={(name) => <span className="text-[12px] text-ink-soft">{t(`cash.series.${String(name)}`)}</span>} iconSize={10} />
                <Bar dataKey="in" fill="var(--color-success)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="out" fill="var(--color-budget)" radius={[3, 3, 0, 0]} isAnimationActive={false} />
                <Line dataKey="net" type="monotone" stroke="var(--color-primary)" strokeWidth={2} dot={{ r: 2 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function SpendingDonut({ o, month }: { o: FinanceOverview; month: string }) {
  const { t } = useTranslation("budget");
  // Ko'p bo'lsa -- eng kattalari va "Boshqa"
  const rows = o.out_categories.map((c) => ({ name: categoryLabel(c, t), value: num(c.amount) })).filter((r) => r.value > 0);
  const top = rows.length > 6 ? [...rows.slice(0, 5), { name: t("cash.other"), value: rows.slice(5).reduce((s, r) => s + r.value, 0) }] : rows;
  const total = top.reduce((s, r) => s + r.value, 0);
  return (
    <Card>
      <CardHeader title={t("cash.spendTitle", { month })} />
      <CardBody>
        {total === 0 ? (
          <p className="py-10 text-center text-[13px] text-muted">{t("cash.spendEmpty")}</p>
        ) : (
          <div className="flex items-center gap-4">
            <div className="h-[150px] w-[150px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={top} dataKey="value" nameKey="name" innerRadius={44} outerRadius={70} paddingAngle={1} stroke="var(--color-surface)" isAnimationActive={false}>
                    {top.map((r, i) => (
                      <Cell key={r.name} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP} formatter={(v, name) => [fmt(Number(v), "USD"), String(name)]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <ul className="min-w-0 flex-1 space-y-1.5 text-[12px]">
              {top.map((r, i) => (
                <li key={r.name} className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: PALETTE[i % PALETTE.length] }} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{r.name}</span>
                  <span className="font-semibold">{fmt(r.value, "USD")}</span>
                  <span className="w-9 text-right text-muted">{Math.round((r.value / total) * 100)}%</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export function DueCard({ o, period }: { o: FinanceOverview; period: string }) {
  const { t } = useTranslation("budget");
  const d = o.due;
  const rows = [
    { key: "expenses", count: d.expenses.count, usd: d.expenses.usd, to: `/budget/expenses?period=${period}&status=approved` },
    { key: "salaries", count: d.salaries.count, usd: d.salaries.usd, to: `/budget/salaries?period=${period}` },
    { key: "bonuses", count: d.bonuses.count, usd: d.bonuses.usd, to: `/budget/bonuses?period=${period}` },
  ].filter((r) => r.count > 0);
  return (
    <Card>
      <CardHeader title={t("cash.dueTitle")} />
      <CardBody className="space-y-3 text-[13px]">
        {/* Qarzdorlik jami (2026-10-18, Bito bilan solishtirish): bizning qarzimiz (xarajat, maosh, bonus) va bizga qarz (carrier) */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-[10px] bg-fill/40 px-3 py-2">
            <div className="text-[11px] text-muted">{t("cash.payable")}</div>
            <div className="text-[15px] font-semibold tabular-nums">{fmt(d.payable_usd, "USD")}</div>
          </div>
          <div className="rounded-[10px] bg-fill/40 px-3 py-2">
            <div className="text-[11px] text-muted">{t("cash.receivable")}</div>
            <div className="text-[15px] font-semibold text-success tabular-nums">{fmt(d.receivable_usd, "USD")}</div>
          </div>
        </div>
        {rows.length === 0 ? (
          <p className="text-muted">{t("cash.nothingDue")}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r) => (
              <li key={r.key} className="flex items-center gap-2">
                <span className="min-w-0 flex-1">{t(`cash.due.${r.key}`, { count: r.count })}</span>
                <span className="font-semibold">{fmt(r.usd, "USD")}</span>
                <Link to={r.to} className="text-[12px] text-primary hover:underline">
                  {t("cash.pay")}
                </Link>
              </li>
            ))}
          </ul>
        )}
        {d.fees.count > 0 && (
          <div className="border-t border-line pt-3">
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1">{t("cash.feesExpected", { count: d.fees.count })}</span>
              <span className="font-semibold text-success">{fmt(d.fees.usd, "USD")}</span>
              <Link to="/budget/placements" className="text-[12px] text-primary hover:underline">
                {t("cash.open")}
              </Link>
            </div>
            {(d.fees.overdue > 0 || d.fees.no_amount > 0) && (
              <div className="mt-0.5 text-[11px] text-muted">
                {[d.fees.overdue > 0 ? t("cash.feesOverdue", { count: d.fees.overdue }) : "", d.fees.no_amount > 0 ? t("cash.feesNoAmount", { count: d.fees.no_amount }) : ""].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
